import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../redux/store";
import { getLastPunch, createPunch } from "../redux/features/punch/PunchApi";
import {
    Clock,
    Sun,
    History as HistoryIcon,
    CheckCircle,
    Settings,
    TrendingUp,
    Calendar,
    Loader2,
    AlertCircle,
    Plus,
} from "lucide-react";
import { getThisWeekTimeCardSummary } from "../redux/features/EmployeeTimeCard";
import { convertDecimalHoursToHM } from "../utils/employeeTimeCardhelper";
import { fetchAttendanceTags } from "../redux/features/common";

export const PunchPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const [currentTime, setCurrentTime] = useState(new Date());
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error" | "info";
    }>({
        open: false,
        message: "",
        severity: "info",
    });

    // Redux state
    const { lastPunch, isLoadingLastPunch, isCreatingPunch, lastPunchError, createPunchError } = useSelector(
        (state: RootState) => state.punch
    );
    const { attendanceTags } = useSelector((state: RootState) => state.common);

    const user = useSelector((state: RootState) => state?.user?.user);
    const { thisWeekSummary, loading: loadingSummary } = useSelector((state: RootState) => state.employeeTimeCardData);
    const [isOptionsOpen, setIsOptionsOpen] = useState(false);
    const [attendanceFormData, setAttendanceFormData] = useState<Record<string, any>>({});
    const [notes, setNotes] = useState<string>("");

    // Update current time every second
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        console.log(attendanceTags);
    }, [attendanceTags]);

    // Load last punch data on component mount
    useEffect(() => {
        dispatch(getLastPunch());
        dispatch(fetchAttendanceTags());
    }, [dispatch]);

    useEffect(() => {
        if (user?.os_emp_account_id) {
            dispatch(getThisWeekTimeCardSummary({ employee_id: Number(user.os_emp_account_id) }));
        }
    }, [dispatch, user?.os_emp_account_id]);

    // Show error messages from Redux
    useEffect(() => {
        if (lastPunchError) {
            showSnackbar(lastPunchError, "error");
        }
        if (createPunchError) {
            showSnackbar(createPunchError, "error");
        }
    }, [lastPunchError, createPunchError]);

    const toggleOptionsSection = () => {
        setIsOptionsOpen((prev) => !prev);
    };

    const showSnackbar = (message: string, severity: "success" | "error" | "info") => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    };

    // Function to validate required attendance tags (only for clock in)
    const validateRequiredAttendanceTags = (isClockIn: boolean = false) => {
        // Only validate required fields for clock in
        if (!isClockIn || !attendanceTags || attendanceTags.length === 0) {
            return { isValid: true, missingFields: [] };
        }

        const missingFields: string[] = [];
        
        attendanceTags.forEach((tag) => {
            if (tag.required === "true") {
                const fieldValue = attendanceFormData[tag.field_code];
                
                // Check if field is empty or has no valid selection
                if (!fieldValue || 
                    (Array.isArray(fieldValue) && fieldValue.length === 0) ||
                    (typeof fieldValue === "object" && (!fieldValue.value_code || !fieldValue.value_text)) ||
                    (typeof fieldValue === "string" && fieldValue.trim() === "")) {
                    missingFields.push(tag.field_name);
                }
            }
        });

        return {
            isValid: missingFields.length === 0,
            missingFields
        };
    };

    // Function to prepare attendance data for API
    const prepareAttendanceData = () => {
        const attendanceData: any = {};

        // Add form data in the format expected by the API
        if (Object.keys(attendanceFormData).length > 0) {
            const formattedTags: Record<string, string | string[]>[] = [];

            Object.entries(attendanceFormData).forEach(([fieldCode, value]) => {
                if (value) {
                    let fieldValue: string | string[] = "";

                    if (Array.isArray(value)) {
                        // Handle array values (multiselect) - keep as array
                        fieldValue = value.map((item: any) => {
                            if (typeof item === "object" && item.value_text) {
                                return item.value_text;
                            }
                            // Fallback for simple string values
                            return String(item);
                        });
                    } else if (typeof value === "object" && value.value_text) {
                        // Handle single object value - use value_text
                        fieldValue = value.value_text;
                    } else {
                        // Handle simple string values
                        fieldValue = String(value);
                    }

                    if (Array.isArray(fieldValue) ? fieldValue.length > 0 : fieldValue.trim()) {
                        // Create object with field_code as key and value as value (string or array)
                        const fieldObject: Record<string, string | string[]> = {};
                        fieldObject[fieldCode] = Array.isArray(fieldValue) ? fieldValue : fieldValue.trim();
                        formattedTags.push(fieldObject);
                    }
                }
            });

            if (formattedTags.length > 0) {
                attendanceData.attendance_tags = formattedTags;
            }
        }

        return Object.keys(attendanceData).length > 0 ? attendanceData : null;
    };

    const handlePunchIn = async () => {
        // Validate required attendance tags first (only for clock in)
        const validation = validateRequiredAttendanceTags(true);
        if (!validation.isValid) {
            showSnackbar(`Please fill in required fields: ${validation.missingFields.join(", ")}`, "error");
            return;
        }

        try {
            const requestBody: any = {
                status: 0,
            };
            const attendanceData = prepareAttendanceData();

            if (attendanceData) {
                requestBody.attendance_tags_json = attendanceData.attendance_tags;
            }

            // Add notes under other_info_json
            if (notes.trim()) {
                requestBody.other_info_json = {
                    punch_notes: notes.trim()
                };
            }

            // console.log("Punch In Request Body:", requestBody);

            await dispatch(createPunch(requestBody)).unwrap();
            dispatch(getLastPunch());
            showSnackbar("Successfully clocked in!", "success");

            // Reset form data after successful punch
            setAttendanceFormData({});
            setNotes("");
            setIsOptionsOpen(false);
        } catch (error) {
            showSnackbar(error as string, "error");
        }
    };

    const handlePunchOut = async () => {
        // No validation required for clock out
        try {
            const requestBody: any = {
                status: 1,
            };
            const attendanceData = prepareAttendanceData();

            if (attendanceData) {
                requestBody.attendance_tags_json = attendanceData.attendance_tags;
            }

            // Add notes under other_info_json
            if (notes.trim()) {
                requestBody.other_info_json = {
                    punch_notes: notes.trim()
                };
            }

            await dispatch(createPunch(requestBody)).unwrap();
            dispatch(getLastPunch());
            showSnackbar("Successfully clocked out!", "success");

            // Reset form data after successful punch
            setAttendanceFormData({});
            setNotes("");
            setIsOptionsOpen(false);
        } catch (error) {
            // error will be the message from rejectWithValue (i.e., response.message from the API)
            showSnackbar(error as string, "error");
        }
    };

    const handleStartBreak = async () => {
        // No validation required for break start
        try {
            const requestBody: any = {
                status: 3,
            };
            const attendanceData = prepareAttendanceData();

            if (attendanceData) {
                requestBody.attendance_tags_json = attendanceData.attendance_tags;
            }

            // Add notes under other_info_json
            if (notes.trim()) {
                requestBody.other_info_json = {
                    punch_notes: notes.trim()
                };
            }

            await dispatch(createPunch(requestBody)).unwrap();
            dispatch(getLastPunch());
            showSnackbar("Break started!", "success");

            // Reset form data after successful punch
            setAttendanceFormData({});
            setNotes("");
            setIsOptionsOpen(false);
        } catch (error) {
            showSnackbar(error as string, "error");
        }
    };

    const handleEndBreak = async () => {
        // No validation required for break end
        try {
            const requestBody: any = {
                status: 2,
            };
            const attendanceData = prepareAttendanceData();

            if (attendanceData) {
                requestBody.attendance_tags_json = attendanceData.attendance_tags;
            }

            // Add notes under other_info_json
            if (notes.trim()) {
                requestBody.other_info_json = {
                    punch_notes: notes.trim()
                };
            }

            await dispatch(createPunch(requestBody)).unwrap();
            dispatch(getLastPunch());
            showSnackbar("Break ended!", "success");

            // Reset form data after successful punch
            setAttendanceFormData({});
            setNotes("");
            setIsOptionsOpen(false);
        } catch (error) {
            showSnackbar(error as string, "error");
        }
    };

    const getGreeting = () => {
        const hour = currentTime.getHours();
        if (hour < 12) return "Good morning";
        if (hour < 17) return "Good afternoon";
        return "Good evening";
    };

    // Determine current punch state based on last punch
    const getCurrentPunchState = () => {
        if (!lastPunch) {
            return { isClockedIn: false, isOnBreak: false };
        }
        if (lastPunch.reset_punch_state) {
            return { isClockedIn: false, isOnBreak: false };
        }
        switch (lastPunch.punch_status) {
            case 0:
                return { isClockedIn: true, isOnBreak: false };
            case 1:
                return { isClockedIn: false, isOnBreak: false };
            case 2:
                return { isClockedIn: true, isOnBreak: false };
            case 3:
                return { isClockedIn: true, isOnBreak: true };
            default:
                return { isClockedIn: false, isOnBreak: false };
        }
    };

    const { isClockedIn, isOnBreak } = getCurrentPunchState();
    const loading = isLoadingLastPunch || isCreatingPunch;
    
    // Check if form is valid for required fields (only for clock in)
    const isFormValid = () => {
        // Only validate for clock in, not for other actions
        if (!isClockedIn) {
            const validation = validateRequiredAttendanceTags(true);
            return validation.isValid;
        }
        return true; // Always valid for clock out and breaks
    };

    const getPrimaryButton = () => {
        if (!isClockedIn) {
            return (
                <button
                    onClick={handlePunchIn}
                    disabled={loading || !isFormValid()}
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold py-3 rounded-xl text-lg shadow-md transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Clock className="w-5 h-5" />}
                    {loading ? "Punching In..." : (!isFormValid() ? "Fill Required Fields" : "Clock In")}
                </button>
            );
        }
        if (isOnBreak) {
            return (
                <button
                    onClick={handleEndBreak}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800 text-white font-semibold py-3 rounded-xl text-lg shadow-md transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Clock className="w-5 h-5" />}
                    {loading ? "Ending Break..." : "End Break"}
                </button>
            );
        }
        return (
            <button
                onClick={handlePunchOut}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 text-white font-semibold py-3 rounded-xl text-lg shadow-md transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Clock className="w-5 h-5" />}
                {loading ? "Punching Out..." : "Clock Out"}
            </button>
        );
    };

    const getSecondaryButton = () => {
        if (!isClockedIn || isOnBreak) return null;
        return (
            <button
                onClick={handleStartBreak}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 border-2 border-indigo-500 text-indigo-600 font-semibold py-3 rounded-xl text-lg shadow-sm hover:bg-indigo-50 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
                Start Break
            </button>
        );
    };

    const getLastPunchText = () => {
        if (!lastPunch) return "No punch history";
        const punchTypes = { 0: "In", 1: "Out", 3: "Break Start", 2: "Break End" };
        return `Last punched ${punchTypes[lastPunch.punch_status]} at ${format(
            new Date(lastPunch.datetime),
            "hh:mm a"
        )}`;
    };

    // Function to render form fields based on attendance tags configuration
    const renderAttendanceField = (tag: (typeof attendanceTags)[0]) => {
        const isMultiselect = tag.multiselect === "true";
        const fieldValue = attendanceFormData[tag.field_code] || (isMultiselect ? [] : "");

        const handleFieldChange = (value: any) => {
            setAttendanceFormData((prev) => ({
                ...prev,
                [tag.field_code]: value,
            }));
        };

        // Parse options from field_json (using AttendanceTagSelector logic)
        const getOptions = (f: typeof tag): { value_code: string; value_text: string }[] => {
            // Use field_json exclusively (following AttendanceTagSelector pattern)
            if (!f.field_json) {
                return [];
            }

            if (Array.isArray(f.field_json)) {
                return f.field_json.map((item: any) => ({
                    value_code: item.field_value_code || item.value_code || item.code || "",
                    value_text: item.field_value_text || item.value_text || item.text || item.name || "",
                }));
            }

            if (typeof f.field_json === "string") {
                try {
                    const arr = JSON.parse(f.field_json);
                    if (Array.isArray(arr)) {
                        return arr.map((item: any) => ({
                            value_code: item.field_value_code || item.value_code || item.code || "",
                            value_text: item.field_value_text || item.value_text || item.text || item.name || "",
                        }));
                    }
                    return [];
                } catch (error) {
                    return [];
                }
            }

            return [];
        };

        // Get field options - only use field_json, no defaults
        const options = getOptions(tag);
        const isRequired = tag.required === "true";
        const fieldType = tag.field_type?.toLowerCase();

        // Always render field - don't filter out based on options
        // Text fields don't need options, dropdowns can be empty initially

        // Toggle field (Radio buttons for Yes/No)
        if (fieldType === "toggle") {
            return (
                <div key={tag.field_code} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                        {tag.field_name}
                        {isRequired && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {options.length > 0 ? (
                        <div className="space-y-2">
                            {options.map((option, index) => (
                                <label key={index} className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name={tag.field_code}
                                        value={option.value_code}
                                        checked={
                                            typeof fieldValue === "object"
                                                ? fieldValue.value_code === option.value_code
                                                : fieldValue === option.value_code
                                        }
                                        onChange={() => {
                                            handleFieldChange({
                                                value_code: option.value_code,
                                                value_text: option.value_text,
                                            });
                                        }}
                                        className="text-indigo-600 focus:ring-indigo-500 border-gray-300"
                                        required={isRequired}
                                    />
                                    <span className="text-sm text-gray-700">{option.value_text}</span>
                                </label>
                            ))}
                        </div>
                    ) : (
                        // If no options configured, provide default Yes/No options for toggle
                        <div className="space-y-2">
                            <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name={tag.field_code}
                                    value="yes"
                                    checked={
                                        typeof fieldValue === "object"
                                            ? fieldValue.value_code === "yes"
                                            : fieldValue === "yes"
                                    }
                                    onChange={() => {
                                        handleFieldChange({
                                            value_code: "yes",
                                            value_text: "Yes",
                                        });
                                    }}
                                    className="text-indigo-600 focus:ring-indigo-500 border-gray-300"
                                    required={isRequired}
                                />
                                <span className="text-sm text-gray-700">Yes</span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name={tag.field_code}
                                    value="no"
                                    checked={
                                        typeof fieldValue === "object"
                                            ? fieldValue.value_code === "no"
                                            : fieldValue === "no"
                                    }
                                    onChange={() => {
                                        handleFieldChange({
                                            value_code: "no",
                                            value_text: "No",
                                        });
                                    }}
                                    className="text-indigo-600 focus:ring-indigo-500 border-gray-300"
                                    required={isRequired}
                                />
                                <span className="text-sm text-gray-700">No</span>
                            </label>
                        </div>
                    )}
                    {tag.field_description && <p className="text-xs text-gray-500">{tag.field_description}</p>}
                </div>
            );
        }

        // Dropdown field with multiselect support
        if (fieldType === "dropdown") {
            // If no options available, render simple single select regardless of multiselect setting
            if (options.length === 0) {
                return (
                    <div key={tag.field_code} className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            {tag.field_name}
                            {isRequired && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <select
                            value=""
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-900"
                            disabled
                        >
                            <option value="">No options available</option>
                        </select>
                        {tag.field_description && <p className="text-xs text-gray-500">{tag.field_description}</p>}
                    </div>
                );
            }

            if (isMultiselect) {
                // Multi-select dropdown
                return (
                    <div key={tag.field_code} className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            {tag.field_name}
                            {isRequired && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <select
                            multiple
                            value={
                                Array.isArray(fieldValue)
                                    ? fieldValue.map((item: any) => (typeof item === "object" ? item.value_code : item))
                                    : []
                            }
                            onChange={(e) => {
                                const selectedValues = Array.from(e.target.selectedOptions).map((option) => {
                                    const selectedOption = options.find((opt) => opt.value_code === option.value);
                                    return selectedOption
                                        ? {
                                              value_code: selectedOption.value_code,
                                              value_text: selectedOption.value_text,
                                          }
                                        : option.value;
                                });
                                handleFieldChange(selectedValues);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-900"
                            size={Math.min(Math.max(options.length, 2), 4)}
                            required={isRequired}
                        >
                            {options.map((option, index) => (
                                <option key={index} value={option.value_code}>
                                    {option.value_text}
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500">Hold Ctrl/Cmd to select multiple options</p>
                        {tag.field_description && <p className="text-xs text-gray-500">{tag.field_description}</p>}
                    </div>
                );
            } else {
                // Single select dropdown
                return (
                    <div key={tag.field_code} className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            {tag.field_name}
                            {isRequired && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <select
                            value={typeof fieldValue === "object" ? fieldValue.value_code || "" : fieldValue}
                            onChange={(e) => {
                                const selectedOption = options.find((opt) => opt.value_code === e.target.value);
                                if (selectedOption) {
                                    handleFieldChange({
                                        value_code: selectedOption.value_code,
                                        value_text: selectedOption.value_text,
                                    });
                                } else {
                                    handleFieldChange("");
                                }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-900"
                            required={isRequired}
                        >
                            <option value="">Select {tag.field_name}...</option>
                            {options.map((option, index) => (
                                <option key={index} value={option.value_code}>
                                    {option.value_text}
                                </option>
                            ))}
                        </select>
                        {tag.field_description && <p className="text-xs text-gray-500">{tag.field_description}</p>}
                    </div>
                );
            }
        }

        // Text field (default)
        return (
            <div key={tag.field_code} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                    {tag.field_name}
                    {isRequired && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                    type="text"
                    value={typeof fieldValue === "object" ? fieldValue.value_text || "" : fieldValue}
                    onChange={(e) => handleFieldChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-900"
                    placeholder={`Enter ${tag.field_name}`}
                    required={isRequired}
                />
                {tag.field_description && <p className="text-xs text-gray-500">{tag.field_description}</p>}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-100 to-blue-100 py-4">
            <div className="max-w-md mx-auto px-2">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">
                            Hello {user?.os_emp_first_name || ""}, {getGreeting()}
                        </h1>
                        <div className="text-gray-500 text-sm">{format(currentTime, "EEEE, MMMM do, yyyy")}</div>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* <div className="flex items-center gap-1">
              <Sun className="w-5 h-5 text-yellow-400" />
              <span className="text-gray-700 font-medium text-sm">72°</span>
            </div>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">J</div> */}
                    </div>
                </div>

                {/* Main Clock Card */}
                <div className="rounded-3xl bg-white/90 shadow-lg border border-white/60 mb-6 backdrop-blur-lg">
                    <div className="p-8 text-center">
                        <div className="font-mono text-4xl sm:text-5xl font-light text-gray-900 mb-3">
                            {format(currentTime, "HH:mm:ss")}
                        </div>
                        <div className="flex items-center justify-center mb-4 gap-4">
                            {/* Punch Labels Section */}
                            <div className="flex items-center justify-center gap-2">
                                <span
                                    className={`px-3 py-1 rounded-full border text-xs font-semibold ${
                                        isClockedIn
                                            ? "border-green-500 text-green-700 bg-green-50"
                                            : "border-gray-300 text-gray-500 bg-gray-100"
                                    }`}
                                >
                                    {isClockedIn ? "Clocked In" : "Not Clocked In"}
                                </span>
                                {isOnBreak && (
                                    <span className="px-3 py-1 rounded-full border border-yellow-400 text-yellow-700 bg-yellow-50 text-xs font-semibold">
                                        On Break
                                    </span>
                                )}
                            </div>
                            {/* Options Icon Section */}
                            <div className="flex items-center justify-center">
                                <Plus
                                    className={`w-6 h-6 cursor-pointer border border-gray-300 rounded-full p-1 transition-all duration-200 ${
                                        isOptionsOpen
                                            ? "text-indigo-600 border-indigo-500 bg-indigo-50 rotate-45"
                                            : "text-gray-400 hover:text-gray-600 hover:border-gray-400"
                                    }`}
                                    onClick={toggleOptionsSection}
                                />
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            {getPrimaryButton()}
                            {getSecondaryButton()}
                        </div>
                        
                        {/* Show required fields warning if options are closed but there are required fields (only for clock in) */}
                        {!isOptionsOpen && !isClockedIn && (() => {
                            const validation = validateRequiredAttendanceTags(true);
                            if (!validation.isValid && attendanceTags && attendanceTags.some(tag => tag.required === "true")) {
                                return (
                                    <div className="mt-3 text-center">
                                        <p className="text-xs text-amber-600 bg-amber-50 px-3 py-1 rounded-full border border-amber-200">
                                            ⚠️ Required attendance fields must be filled before clocking in
                                        </p>
                                    </div>
                                );
                            } else if (validation.isValid && attendanceTags && attendanceTags.some(tag => tag.required === "true")) {
                                return (
                                    <div className="mt-3 text-center">
                                        <p className="text-xs text-green-600 bg-green-50 px-3 py-1 rounded-full border border-green-200">
                                            ✅ All required fields are filled
                                        </p>
                                    </div>
                                );
                            }
                            return null;
                        })()}
                    </div>
                </div>

                {/* Attendance Tags Options Section */}
                {isOptionsOpen && (
                    <div className="rounded-2xl bg-white/80 shadow border border-white/50 mb-6 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-800">Attendance Options</h3>
                        </div>

                        {/* Required fields summary (only for clock in) */}
                        {!isClockedIn && (() => {
                            const validation = validateRequiredAttendanceTags(true);
                            if (!validation.isValid) {
                                return (
                                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                        <p className="text-sm font-medium text-red-700 mb-1">
                                            Required fields must be filled for clock in:
                                        </p>
                                        <ul className="text-xs text-red-600">
                                            {validation.missingFields.map((field, index) => (
                                                <li key={index}>• {field}</li>
                                            ))}
                                        </ul>
                                    </div>
                                );
                            }
                            return null;
                        })()}

                        <div className="space-y-4">
                            {/* Render attendance tag fields */}
                            {attendanceTags && attendanceTags.length > 0 ? (
                                attendanceTags.map((tag) => renderAttendanceField(tag))
                            ) : (
                                <div className="text-center py-4">
                                    <p className="text-sm text-gray-500">No attendance fields configured</p>
                                </div>
                            )}

                            {/* Notes section */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">Notes</label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-900 resize-none"
                                    placeholder="Add any notes for this punch..."
                                    rows={3}
                                />
                            </div>

                            {/* Show current form data for debugging */}
                            {(Object.keys(attendanceFormData).length > 0 || notes) && (
                                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                    <p className="text-sm font-medium text-gray-700 mb-2">Current Selections:</p>
                                    {Object.entries(attendanceFormData).map(([key, value]) => {
                                        const fieldName =
                                            attendanceTags?.find((tag) => tag.field_code === key)?.field_name || key;

                                        let displayValue = "";
                                        if (Array.isArray(value)) {
                                            // Handle array of objects - show value_text
                                            displayValue = value
                                                .map((item: any) =>
                                                    typeof item === "object" && item.value_text ? item.value_text : item
                                                )
                                                .join(", ");
                                        } else if (typeof value === "object" && value.value_text) {
                                            // Handle single object - show value_text
                                            displayValue = value.value_text;
                                        } else {
                                            // Handle simple string values
                                            displayValue = String(value);
                                        }

                                        return (
                                            <p key={key} className="text-xs text-gray-600">
                                                {fieldName}: {displayValue}
                                            </p>
                                        );
                                    })}
                                    {notes && <p className="text-xs text-gray-600">Notes: {notes}</p>}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Pay Period Summary Card (replaces old summary) */}
                <div className="rounded-2xl bg-white/80 shadow border border-white/50 mb-6 p-6 relative">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-lg font-semibold text-gray-800">Pay Period Summary</div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 mb-2">
                        <div className="text-center">
                            <div className="text-xl font-bold text-indigo-600">
                                {loadingSummary ? (
                                    <Loader2 className="w-5 h-5 animate-spin inline" />
                                ) : (
                                    convertDecimalHoursToHM(thisWeekSummary?.total_hours ?? 0)
                                )}
                            </div>
                            <div className="text-xs text-gray-500">Total Worked (This Week)</div>
                        </div>
                    </div>
                </div>

                {/* Snackbar for notifications */}
                {snackbar.open && (
                    <div className={`fixed left-1/2 bottom-8 z-50 -translate-x-1/2 w-full max-w-xs px-4`}>
                        <div
                            className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white ${
                                snackbar.severity === "success"
                                    ? "bg-green-600"
                                    : snackbar.severity === "error"
                                    ? "bg-red-600"
                                    : "bg-blue-600"
                            }`}
                        >
                            {snackbar.severity === "error" && <AlertCircle className="w-5 h-5 text-white" />}
                            <span className="flex-1">{snackbar.message}</span>
                            <button
                                onClick={() => setSnackbar((s) => ({ ...s, open: false }))}
                                className="ml-2 text-white/80 hover:text-white"
                            >
                                &times;
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
